/**
 * Repositories over the IndexedDB stores. These are the only place the rest of the app touches
 * `idb`; everything above works with plain contract objects.
 *
 * Rules honoured here:
 * - clips live once, globally; projects only reference clip ids (SPEC "Animation Library"),
 * - a multi-record change is one transaction, so a failure leaves nothing half written,
 * - every write is validated first (`./validate`).
 */

import {
  AppError,
  newId,
  type CanonicalAnimationClip,
  type CanonicalRigPlacement,
  type MappingPreset,
  type ProjectRecord,
  type RigBinding,
  type SkinData,
} from '../shared';
import { withDb, type IntchMotionKitDatabase, type BlobRecord } from './db';
import {
  assertStorablePreset,
  assertStorableProject,
  normalizeClipForStorage,
  normalizeProjectForRead,
} from './validate';

function uniqueStrings(values: readonly string[]): string[] {
  return [...new Set(values)];
}

function byUpdatedAtDesc(a: ProjectRecord, b: ProjectRecord): number {
  if (b.updatedAt !== a.updatedAt) return b.updatedAt - a.updatedAt;
  return a.id < b.id ? -1 : a.id > b.id ? 1 : 0;
}

/** Abort a transaction we are about to bail out of, without leaving `tx.done` unhandled. */
function abortQuietly(tx: { abort(): void; done: Promise<unknown> }): void {
  tx.done.catch(() => undefined);
  tx.abort();
}

/**
 * Abort after a failure, tolerating a transaction that has already finished.
 *
 * IndexedDB does NOT abort a transaction because a `put` threw — a synchronous `DataCloneError`
 * leaves every request issued before it committed (measured; see `.claude/vault/testing-quirks`).
 * Rolling back therefore has to be explicit, and by then the transaction may equally well be
 * gone, which makes `abort()` throw in turn.
 */
function abortAfterFailure(tx: { abort(): void; done: Promise<unknown> }): void {
  tx.done.catch(() => undefined);
  try {
    tx.abort();
  } catch {
    // Already committed or already aborted — nothing left to roll back.
  }
}

async function mutateProject(
  db: IntchMotionKitDatabase,
  id: string,
  mutate: (record: ProjectRecord) => ProjectRecord,
): Promise<ProjectRecord> {
  const tx = db.transaction('projects', 'readwrite');
  const stored = await tx.store.get(id);
  if (!stored) {
    abortQuietly(tx);
    throw new AppError('PROJECT_NOT_FOUND', `No project with id "${id}".`, { id });
  }
  // Repair on the way in: a record written by an older build must not make every later write
  // fail validation on a field this change is not even touching.
  const { record: existing, problems } = normalizeProjectForRead(stored);
  if (!existing) {
    abortQuietly(tx);
    throw new AppError(
      'PROJECT_CORRUPT',
      `The stored data for project "${id}" is unreadable (${problems.join('; ')}).`,
      { id, problems },
    );
  }
  /*
   * The repairs above are about to become PERMANENT (audit §2.3 #2). `next` is built from the
   * REPAIRED record, so a nulled binding or a dropped weight set stops being a read-time
   * convenience the moment this write lands: the record on disk loses what triage could not
   * read, and there is no project backup to go back to.
   *
   * The write is not refused. Refusing it was considered and rejected: a project that can be
   * opened but never edited is a project the user has lost anyway, and they are the ones who
   * should decide what to do about the damage. What the old code did wrong was doing it in
   * SILENCE — `problems` was destructured and thrown away here. So it is reported: to the
   * console from inside the repository (which has no route to the UI), and to the user at OPEN
   * time by `projectController`, which reads `projectsRepo.readProblems` before the record is
   * loaded and names what was dropped. Open time is the right moment because it is the last one
   * at which the user still has the choice.
   */
  if (problems.length > 0) {
    console.warn(
      `[intch-motion-kit] project "${id}" was repaired on read; this write makes the repair permanent`,
      problems,
    );
  }
  const next: ProjectRecord = { ...mutate(existing), updatedAt: Date.now() };
  assertStorableProject(next);
  await Promise.all([tx.store.put(next), tx.done]);
  return next;
}

// ---------------------------------------------------------------------------
// Projects
// ---------------------------------------------------------------------------

export const projectsRepo = {
  /**
   * Newest first (most recently updated project is what the user wants to reopen).
   *
   * A record that cannot be made usable is left out rather than allowed to break the list: the
   * project browser is the screen the user reaches for when something else has already gone
   * wrong, so it is the last screen that may throw.
   */
  async list(): Promise<ProjectRecord[]> {
    return withDb(async (db) => {
      const all = await db.getAllFromIndex('projects', 'updatedAt');
      const usable: ProjectRecord[] = [];
      for (const raw of all) {
        const { record, problems } = normalizeProjectForRead(raw);
        if (!record) {
          console.error('[intch-motion-kit] skipping an unreadable project record', { raw, problems });
          continue;
        }
        if (problems.length > 0) {
          console.warn(`[intch-motion-kit] project "${record.id}" was repaired on read`, problems);
        }
        usable.push(record);
      }
      return usable.sort(byUpdatedAtDesc);
    });
  },

  /**
   * Read one project, repairing what can be repaired. Throws `PROJECT_CORRUPT` when the record
   * is beyond repair — a named failure the UI can show, rather than a crash somewhere down the
   * reopen path (audit P1).
   */
  async get(id: string): Promise<ProjectRecord | undefined> {
    return withDb(async (db) => {
      const raw = await db.get('projects', id);
      if (raw === undefined) return undefined;
      const { record, problems } = normalizeProjectForRead(raw);
      if (!record) {
        throw new AppError(
          'PROJECT_CORRUPT',
          `The stored data for this project is unreadable (${problems.join('; ')}). ` +
            'Re-import the model.',
          { id, problems },
        );
      }
      if (problems.length > 0) {
        console.warn(`[intch-motion-kit] project "${id}" was repaired on read`, problems);
      }
      return record;
    });
  },

  /**
   * What `get` had to repair, without opening anything. For the UI to warn before a reopen.
   *
   * `projectController.openProject` calls this BEFORE `get`, and the ordering is the whole point
   * (audit §2.3 #2). Opening a project can commit the repairs by itself: the unrigged branch
   * dispatches `setPlacement`, whose subscription saves immediately, and that save is a
   * read-modify-write of the repaired record. Read afterwards, this would truthfully report "no
   * problems" about a record that had just lost the user's bone mapping. So the question has to
   * be asked while the answer is still on disk.
   */
  async readProblems(id: string): Promise<string[]> {
    return withDb(async (db) => {
      const raw = await db.get('projects', id);
      if (raw === undefined) return [];
      return normalizeProjectForRead(raw).problems;
    });
  },

  /**
   * Store a GLB and the project that references it in ONE transaction (audit P1).
   *
   * Doing it as two writes meant a project write that failed — quota, a closing connection, a
   * validation throw — left a multi-megabyte blob in IndexedDB that nothing referenced and
   * nothing would ever collect. `makeRecord` receives the blob id it has to reference; if the
   * project cannot be written the transaction aborts and the blob is never there in the first
   * place.
   */
  async createWithBlob(
    blob: Blob,
    makeRecord: (blobId: string) => ProjectRecord,
    mime = 'model/gltf-binary',
  ): Promise<ProjectRecord> {
    const blobRecord: BlobRecord = {
      id: newId(),
      blob,
      mime,
      size: blob.size,
      createdAt: Date.now(),
    };
    const record: ProjectRecord = { ...makeRecord(blobRecord.id), updatedAt: Date.now() };
    // Validate before the transaction opens: an abort mid-write is a rollback we would rather
    // not depend on, and this way a bad record costs nothing.
    assertStorableProject(record);
    if (record.sourceGlbBlobId !== blobRecord.id) {
      throw new AppError(
        'INVALID_PROJECT',
        'A project created with its GLB must reference that blob.',
        { expected: blobRecord.id, actual: record.sourceGlbBlobId },
      );
    }

    return withDb(async (db) => {
      const tx = db.transaction(['projects', 'blobs'], 'readwrite');
      try {
        // The project goes first deliberately: if writing it throws synchronously, the blob
        // request is never even issued. The explicit abort below covers the asynchronous
        // failures (quota, a closing connection), which IndexedDB does not roll back by itself.
        await Promise.all([
          tx.objectStore('projects').put(record),
          tx.objectStore('blobs').put(blobRecord),
          tx.done,
        ]);
      } catch (error) {
        abortAfterFailure(tx);
        throw error;
      }
      return record;
    });
  },

  /** Stamps `updatedAt` and returns the record exactly as stored. */
  async put(record: ProjectRecord): Promise<ProjectRecord> {
    const next: ProjectRecord = { ...record, updatedAt: Date.now() };
    assertStorableProject(next);
    return withDb(async (db) => {
      await db.put('projects', next);
      return next;
    });
  },

  /**
   * Stamp "the user opened this project just now" — and nothing else.
   *
   * Deliberately not routed through `put` / `mutateProject`: both stamp `updatedAt`, and opening
   * a project is not editing it. Letting an open move `updatedAt` would make the project list
   * (sorted by it) reorder itself just for being looked at, and would falsely claim the record
   * changed at that moment for anything else reading the timestamp.
   *
   * A record that is gone, or is not an object at all, is a silent no-op: this is metadata for a
   * list, and it is never worth failing an open the user did successfully complete.
   *
   * The stored record is stamped **raw** — NOT the result of `normalizeProjectForRead`, and
   * without `assertStorableProject` over the whole record. Read-time triage is a reading
   * convenience that keeps a damaged project usable; committing it from here would mean that
   * merely OPENING a project permanently deletes the damaged parts (a corrupt binding would
   * become `binding: null` on disk, corrupt weights would be dropped) without the user editing
   * anything. Repairs stay in memory until a deliberate edit writes through `put`/`mutateProject`.
   */
  async markOpened(id: string): Promise<void> {
    return withDb(async (db) => {
      const tx = db.transaction('projects', 'readwrite');
      const stored = await tx.store.get(id);
      // Anything that is not a plain object cannot be spread without inventing a record; leave it
      // exactly as it is and let read-time triage keep quarantining it.
      if (typeof stored !== 'object' || stored === null || Array.isArray(stored)) {
        abortQuietly(tx);
        return;
      }
      const lastOpenedAt = Date.now();
      // The only field this write introduces is the only field it validates. (A test that stubs
      // the clock can hand us NaN, and writing a value the validator would reject is exactly the
      // corruption `assertStorableProject` exists to prevent.)
      if (!Number.isFinite(lastOpenedAt) || lastOpenedAt < 0) {
        abortQuietly(tx);
        return;
      }
      await Promise.all([tx.store.put({ ...stored, lastOpenedAt }), tx.done]);
    });
  },

  /** Deletes the project and the GLB blobs only it referenced, in one transaction. */
  async remove(id: string): Promise<void> {
    return withDb(async (db) => {
      const tx = db.transaction(['projects', 'blobs'], 'readwrite');
      const projects = tx.objectStore('projects');
      const record = await projects.get(id);
      if (!record) {
        abortQuietly(tx);
        return;
      }
      const blobIds = uniqueStrings(
        [record.sourceGlbBlobId, record.riggedGlbBlobId].filter(
          (b): b is string => typeof b === 'string' && b.length > 0,
        ),
      );
      const blobs = tx.objectStore('blobs');
      await Promise.all([
        projects.delete(id),
        ...blobIds.map((blobId) => blobs.delete(blobId)),
        tx.done,
      ]);
    });
  },

  async setSelectedClips(id: string, clipIds: readonly string[]): Promise<ProjectRecord> {
    return withDb((db) =>
      mutateProject(db, id, (record) => ({ ...record, selectedClipIds: uniqueStrings(clipIds) })),
    );
  },

  async setBinding(id: string, binding: RigBinding | null): Promise<ProjectRecord> {
    return withDb((db) => mutateProject(db, id, (record) => ({ ...record, binding })));
  },

  async setPlacement(id: string, placement: CanonicalRigPlacement | null): Promise<ProjectRecord> {
    return withDb((db) => mutateProject(db, id, (record) => ({ ...record, placement })));
  },

  /** `undefined` removes the stored skin weights (back to "not skinned yet"). */
  async setSkinData(id: string, skinData: SkinData[] | undefined): Promise<ProjectRecord> {
    return withDb((db) =>
      mutateProject(db, id, (record) => {
        const next = { ...record };
        if (skinData === undefined) delete next.skinData;
        else next.skinData = skinData;
        return next;
      }),
    );
  },
};

// ---------------------------------------------------------------------------
// Blobs
// ---------------------------------------------------------------------------

export const blobsRepo = {
  /** Stores binary payloads (GLB) out of line and returns the generated id. */
  async put(blob: Blob, mime = 'model/gltf-binary'): Promise<string> {
    const record: BlobRecord = {
      id: newId(),
      blob,
      mime,
      size: blob.size,
      createdAt: Date.now(),
    };
    return withDb(async (db) => {
      await db.put('blobs', record);
      return record.id;
    });
  },

  async get(id: string): Promise<Blob | undefined> {
    return withDb(async (db) => (await db.get('blobs', id))?.blob);
  },

  async getRecord(id: string): Promise<BlobRecord | undefined> {
    return withDb((db) => db.get('blobs', id));
  },

  /**
   * Byte size of a stored payload, `undefined` when there is no such blob. The size was recorded
   * next to the bytes at write time (`BlobRecord.size`), so the answer comes from the record's
   * own field — nothing reads or decodes the GLB to produce it.
   */
  async sizeOf(id: string): Promise<number | undefined> {
    return withDb(async (db) => (await db.get('blobs', id))?.size);
  },

  async remove(id: string): Promise<void> {
    return withDb((db) => db.delete('blobs', id));
  },
};

// ---------------------------------------------------------------------------
// Clips (the global animation library)
// ---------------------------------------------------------------------------

/**
 * The `name` index of the `clips` store, as the uniqueness helpers below use it. Declared
 * structurally so the helpers work with an `IDBPIndex` from any transaction flavour.
 */
interface ClipNameIndex {
  getAllKeys(query: string): Promise<IDBValidKey[]>;
}

/**
 * Clip names are unique in the library (audit P1). They are not decoration: `play("Walk")` in the
 * runtime and the exported GLB's track names are both name-based, and two clips called "Walk"
 * make the first of those silently ambiguous. `selfId` is the clip being written, so re-saving a
 * clip under its own name is not a collision with itself.
 */
async function assertClipNameFree(
  index: ClipNameIndex,
  name: string,
  selfId: string,
): Promise<void> {
  const keys = await index.getAllKeys(name);
  const clash = keys.find((key) => key !== selfId);
  if (clash === undefined) return;
  throw new AppError(
    'DUPLICATE_CLIP_NAME',
    `The library already has an animation called "${name}". Names must be unique.`,
    { name, existingId: String(clash) },
  );
}

/**
 * `"Idle"` → `"Idle 2"` → `"Idle 3"`, resolved against what is actually in the store rather than
 * against a UI cache. For the paths that generate a name (duplicate, library import) — the paths
 * where the user did not type anything, so there is nothing to reject them over.
 */
export async function freeClipName(
  index: ClipNameIndex,
  wanted: string,
  selfId: string | null = null,
  alsoTaken: ReadonlySet<string> = new Set(),
): Promise<string> {
  const base = wanted.trim() || 'new clip';
  const taken = async (name: string): Promise<boolean> => {
    if (alsoTaken.has(name)) return true;
    return (await index.getAllKeys(name)).some((key) => key !== selfId);
  };
  if (!(await taken(base))) return base;
  for (let n = 2; n < 1000; n++) {
    const candidate = `${base} ${n}`;
    if (!(await taken(candidate))) return candidate;
  }
  return `${base} ${Date.now()}`;
}

export const clipsRepo = {
  /** Alphabetical by name — this is what the library panel shows. */
  async list(): Promise<CanonicalAnimationClip[]> {
    return withDb((db) => db.getAllFromIndex('clips', 'name'));
  },

  async get(id: string): Promise<CanonicalAnimationClip | undefined> {
    return withDb((db) => db.get('clips', id));
  },

  async exists(id: string): Promise<boolean> {
    return withDb(async (db) => (await db.getKey('clips', id)) !== undefined);
  },

  /**
   * Validates + normalizes, stamps `updatedAt`, returns the stored clip. Throws
   * `DUPLICATE_CLIP_NAME` when another clip already holds the name — the caller (the user, at a
   * name field) is the one who should resolve that, not a silent auto-rename of their work.
   */
  async put(clip: CanonicalAnimationClip): Promise<CanonicalAnimationClip> {
    const next: CanonicalAnimationClip = {
      ...normalizeClipForStorage(clip),
      updatedAt: Date.now(),
    };
    return withDb(async (db) => {
      const tx = db.transaction('clips', 'readwrite');
      try {
        await assertClipNameFree(tx.store.index('name'), next.name, next.id);
      } catch (error) {
        abortQuietly(tx);
        throw error;
      }
      await Promise.all([tx.store.put(next), tx.done]);
      return next;
    });
  },

  /**
   * Atomic batch write: every clip is validated before the transaction opens, so one bad clip
   * means nothing at all is written.
   */
  async putMany(clips: readonly CanonicalAnimationClip[]): Promise<CanonicalAnimationClip[]> {
    const now = Date.now();
    // Validate the whole batch BEFORE opening the transaction: a throw inside the loop below
    // would leave the already-issued puts committed (verified — IDB does not roll them back).
    const prepared = clips.map((clip) => ({ ...normalizeClipForStorage(clip), updatedAt: now }));

    const withinBatch = new Map<string, string>();
    for (const clip of prepared) {
      const owner = withinBatch.get(clip.name);
      if (owner !== undefined && owner !== clip.id) {
        throw new AppError(
          'DUPLICATE_CLIP_NAME',
          `This batch writes two different animations called "${clip.name}".`,
          { name: clip.name, ids: [owner, clip.id] },
        );
      }
      withinBatch.set(clip.name, clip.id);
    }

    return withDb(async (db) => {
      const tx = db.transaction('clips', 'readwrite');
      try {
        const index = tx.store.index('name');
        for (const clip of prepared) await assertClipNameFree(index, clip.name, clip.id);
      } catch (error) {
        abortQuietly(tx);
        throw error;
      }
      await Promise.all([...prepared.map((clip) => tx.store.put(clip)), tx.done]);
      return prepared;
    });
  },

  /**
   * Deletes the clip from the library only. Project selections may still reference it — use
   * `removeClipEverywhere` unless you are deliberately keeping the references.
   */
  async remove(id: string): Promise<void> {
    return withDb((db) => db.delete('clips', id));
  },

  async rename(id: string, name: string): Promise<CanonicalAnimationClip> {
    const trimmed = name.trim();
    if (trimmed.length === 0) {
      throw new AppError('INVALID_CLIP_NAME', 'An animation name cannot be empty.', { id });
    }
    return withDb(async (db) => {
      const tx = db.transaction('clips', 'readwrite');
      const existing = await tx.store.get(id);
      if (!existing) {
        abortQuietly(tx);
        throw new AppError('CLIP_NOT_FOUND', `No animation with id "${id}".`, { id });
      }
      try {
        await assertClipNameFree(tx.store.index('name'), trimmed, id);
      } catch (error) {
        abortQuietly(tx);
        throw error;
      }
      const next: CanonicalAnimationClip = { ...existing, name: trimmed, updatedAt: Date.now() };
      await Promise.all([tx.store.put(next), tx.done]);
      return next;
    });
  },

  /**
   * New id, `" copy"` suffix by default; keyframes are deep-copied. The name is resolved against
   * the store to the first free variant — this path invents the name, so there is nobody to
   * hand a collision error to.
   */
  async duplicate(id: string, newName?: string): Promise<CanonicalAnimationClip> {
    return withDb(async (db) => {
      const tx = db.transaction('clips', 'readwrite');
      const existing = await tx.store.get(id);
      if (!existing) {
        abortQuietly(tx);
        throw new AppError('CLIP_NOT_FOUND', `No animation with id "${id}".`, { id });
      }
      const now = Date.now();
      const wanted = newName?.trim() || `${existing.name} copy`;
      const name = await freeClipName(tx.store.index('name'), wanted);
      const copy: CanonicalAnimationClip = {
        ...normalizeClipForStorage(existing),
        id: newId(),
        name,
        createdAt: now,
        updatedAt: now,
      };
      await Promise.all([tx.store.put(copy), tx.done]);
      return copy;
    });
  },

  /**
   * Delete a clip everywhere: the library entry plus every project selection pointing at it.
   * Returns the ids of the projects that changed, so the UI can tell the user what it touched.
   */
  async removeClipEverywhere(id: string): Promise<string[]> {
    return withDb(async (db) => {
      const tx = db.transaction(['clips', 'projects'], 'readwrite');
      const projectsStore = tx.objectStore('projects');
      const projects = await projectsStore.getAll();
      const affected = projects.filter((p) => p.selectedClipIds.includes(id));
      const now = Date.now();
      await Promise.all([
        tx.objectStore('clips').delete(id),
        ...affected.map((project) =>
          projectsStore.put({
            ...project,
            selectedClipIds: project.selectedClipIds.filter((clipId) => clipId !== id),
            updatedAt: now,
          }),
        ),
        tx.done,
      ]);
      return affected.map((p) => p.id);
    });
  },
};

// ---------------------------------------------------------------------------
// Mapping presets
// ---------------------------------------------------------------------------

export const presetsRepo = {
  /** Alphabetical by name. */
  async list(): Promise<MappingPreset[]> {
    return withDb((db) => db.getAllFromIndex('mappingPresets', 'name'));
  },

  async get(id: string): Promise<MappingPreset | undefined> {
    return withDb((db) => db.get('mappingPresets', id));
  },

  async put(preset: MappingPreset): Promise<MappingPreset> {
    assertStorablePreset(preset);
    return withDb(async (db) => {
      await db.put('mappingPresets', preset);
      return preset;
    });
  },

  async remove(id: string): Promise<void> {
    return withDb((db) => db.delete('mappingPresets', id));
  },

  /** Exact name match; `undefined` when no preset uses that name. */
  async getByName(name: string): Promise<MappingPreset | undefined> {
    return withDb((db) => db.getFromIndex('mappingPresets', 'name', name));
  },
};

// ---------------------------------------------------------------------------
// Settings
// ---------------------------------------------------------------------------

export const settingsRepo = {
  /** Returns `fallback` when the key was never written. */
  async get<T>(key: string, fallback: T): Promise<T> {
    return withDb(async (db) => {
      const value = await db.get('settings', key);
      return value === undefined ? fallback : (value as T);
    });
  },

  async set<T>(key: string, value: T): Promise<void> {
    return withDb(async (db) => {
      await db.put('settings', value, key);
    });
  },

  async remove(key: string): Promise<void> {
    return withDb((db) => db.delete('settings', key));
  },
};
